import { observable, action, computed, makeObservable } from 'mobx'
import Workspace from 'iam/models/Workspace'
import { validationContext, required, pattern, func } from 'validx'
import { task } from 'mobx-task'
import { AxiosError } from 'axios'
import { Membership } from 'iam/WorkspaceMembershipStore'
import { Id } from 'types/definitions'
import { AlertFactory } from 'alerts/Alert'
import Member from 'iam/models/Member'

export type ConfigurerViewType = 'search' | 'register'

export interface WorkspaceSignupStatePorts {
  fetchWorkspaceBySlug(slug: string): Promise<Workspace | null>

  fetchWorkspaceById(id: Id): Promise<Workspace | null>

  updateMember(member: Member, profileInfo: ProfileInfo): Promise<void>

  createMember(workspace: Workspace, profileInfo: ProfileInfo): Promise<void>

  fetchMemberFromSession(workspaceId: Id): Promise<Member | null>

  checkMemberExists(workspace: Workspace): Promise<boolean>

  getMemberships(): Membership[]

  getAvailableProfileInfo(): Partial<ProfileInfo>

  reportWorkspaceSelected(workspace: Workspace): Promise<void>

  newAlert: AlertFactory
}

export interface ProfileInfo {
  given_name: string
  family_name: string
  phone: string
  email: string
}

export default class WorkspaceSignupState {
  @observable configurerView: ConfigurerViewType = 'search'
  @observable isConfigurerOpen = false
  @observable givenName = ''
  @observable familyName = ''
  @observable email = ''
  @observable phone = ''
  @observable slug = ''
  @observable lookUpWorkspace: Workspace | null = null
  @observable workspace: Workspace | null = null

  @observable searchError = false
  @observable searchText = ''

  validation = validationContext<WorkspaceSignupState>(this, {
    givenName: [
      required(),
      func(({ value }) => {
        return (
          value.length <= 40 || "Given name can't be more than 40 characters"
        )
      }),
      func(({ value }) => {
        return value.trim() !== '' || 'This field is required'
      }),
    ],
    familyName: [
      required(),
      func(({ value }) => {
        return (
          value.length <= 40 || "Family name can't be more than 40 characters"
        )
      }),
      func(({ value }) => {
        return value.trim() !== '' || 'This field is required'
      }),
    ],
    email: [
      required(),
      pattern('email'),
      func(({ value }) => {
        return value.length <= 320 || "Email can't be more than 320 characters"
      }),
    ],
    phone: [
      required(),
      func(({ value }) => {
        return value.length <= 20 || "Phone can't be more than 20 characters"
      }),
      func(({ value }) => {
        return value.trim() !== '' || 'This field is required'
      }),
    ],
  })

  constructor(private adapter: WorkspaceSignupStatePorts) {
    makeObservable(this)
  }

  /**
   * Activates the store, initializing all the things.
   */
  activate = action(async () => {
    const profileInfo = this.adapter.getAvailableProfileInfo()

    this.email = profileInfo.email || ''
    this.givenName = profileInfo.given_name || ''
    this.familyName = profileInfo.family_name || ''
    this.phone = profileInfo.phone || ''

    if (this.validation.reset().validate().isValid) {
      await this.trySyncMember()
    } else {
      // reset it so that we dont show error messages right away
      this.validation.reset()
    }
  })

  @computed
  get memberships() {
    return this.adapter.getMemberships()
  }

  @action
  setConfigurerView(option: ConfigurerViewType) {
    this.configurerView = option
  }

  @action
  toggleConfigurerOpen(open?: boolean) {
    this.isConfigurerOpen = open === undefined ? !this.isConfigurerOpen : open
  }

  @action
  openWorkspaceSearch() {
    this.setConfigurerView('search')
    this.isConfigurerOpen = true
  }

  @action
  closeWorkspaceSearch() {
    this.slug = ''
    if (this.fetchWorkspace.rejected) {
      this.validation.reset()
      this.fetchWorkspace.reset()
    }
    this.isConfigurerOpen = false
  }

  @computed
  get canSearchWorkspace() {
    return this.slug.trim().length !== 0
  }

  fetchWorkspace = task.resolved(async () => {
    return this.adapter.fetchWorkspaceBySlug(this.slug).then(
      action((found) => {
        this.lookUpWorkspace = found
        if (found && this.memberships.some((m) => m.workspace === found)) {
          throw new Error('You are already a member of this workspace.')
        }

        this.joinWorkspace()
      }),
    )
  })

  joinWorkspace = task(async () => {
    if (!this.lookUpWorkspace) {
      this.searchError = true
      return
    }
    const workspace = await this.adapter.fetchWorkspaceById(
      Id.assert(this.lookUpWorkspace.id),
    )

    if (!workspace) {
      throw new Error('The workspace you selected does not exist.')
    }
    this.setWorkspace(workspace)
    if (!(await this.adapter.checkMemberExists(workspace))) {
      return
    }
    this.adapter.reportWorkspaceSelected(workspace)
  })

  /**
   * @param workspace The workspace to sign up for.
   */
  async maybeSyncMember(workspace: Workspace) {
    this.setWorkspace(workspace)
    // Try to create the member if we already have their info
    if (await this.trySyncMember()) {
      return
    }
    // ... If not, show the registration form.
    this.toggleConfigurerOpen(true)
    this.setConfigurerView('register')
  }

  @action.bound
  setWorkspace(workspace: Workspace) {
    this.workspace = workspace
  }

  trySyncMember = task.resolved(async (): Promise<boolean> => {
    if (!this.workspace) {
      // This member is already associated to a workspace.
      return false
    }

    if (this.validation.reset().validate().isValid === false) {
      return false
    }

    const profileInfo: ProfileInfo = {
      given_name: this.givenName.trim(),
      family_name: this.familyName.trim(),
      email: this.email.trim(),
      phone: this.phone.trim(),
    }
    const member = await this.adapter.fetchMemberFromSession(this.workspace.id)
    if (!member || !member.internalId) {
      await this.adapter
        .createMember(this.workspace, profileInfo)
        .catch((error: any) => {
          this.adapter.newAlert().fromError(error).show()
          throw error
        })
    } else {
      await this.adapter
        .updateMember(member, profileInfo)
        .catch((error: AxiosError) => {
          this.adapter.newAlert().fromError(error).show()
          throw error
        })
    }

    this.toggleConfigurerOpen(false)
    await this.adapter.reportWorkspaceSelected(this.workspace)
    return true
  })
}
